import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerInteractions } from '@/api/client';

interface Interaction {
  interaction_id: string;
  customer_id: string;
  timestamp: string;
  direction: 'inbound' | 'outbound';
  channel: string;
  message_type: string;
  selected_action?: string;
  related_invoice_ids?: string[];
  related_receipt_ids?: string[];
  related_payment_link_id?: string;
  message_text?: string;
  status: string;
  destination?: string;
  contact_name?: string;
  contact_role?: string;
  error_details?: string;
}

interface Props {
  customerId: string;
  refreshToken?: number;
}

const CustomerInteractionLog: React.FC<Props> = ({ customerId, refreshToken = 0 }) => {
  const { t } = useTranslation();
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    loadInteractions();
    return () => { generation.current += 1; };
  }, [customerId, refreshToken]);

  const loadInteractions = async () => {
    const current = ++generation.current;
    try {
      setLoading(true);
      setError(false);
      const data = await getCustomerInteractions(customerId);
      if (current !== generation.current) return;
      setInteractions(data.items || []);
      setLastKey(data.lastEvaluatedKey || null);
    } catch (err) {
      if (current !== generation.current) return;
      console.error('Failed to load interactions:', err);
      setError(true);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!lastKey) return;
    const current = generation.current;
    try {
      const data = await getCustomerInteractions(customerId, 50, lastKey);
      if (current !== generation.current) return;
      setInteractions(prev => [...prev, ...(data.items || [])]);
      setLastKey(data.lastEvaluatedKey || null);
    } catch (err) {
      if (current !== generation.current) return;
      console.error('Failed to load more interactions:', err);
      setError(true);
    }
  };

  const formatTimestamp = (ts: string) => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const getDirectionIcon = (dir: string) => dir === 'inbound' ? '↙️' : '↗️';

  const getTypeLabel = (type: string) => {
    return t(`customerBot.interactionTypes.${type}`, type);
  };

  const getChannelLabel = (channel: string) => {
    return t(`customerBot.channels.${channel}`, channel);
  };

  const getStatusColor = (status: string) => {
    if (status === 'success') return 'text-green-600';
    if (status === 'error') return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return <div className="py-4 text-center text-gray-500">{t('common.loading', 'Loading...')}</div>;
  }

  if (!interactions.length) {
    return (
      <div className="py-6 text-center text-gray-400">
        {error ? <div role="alert" className="text-red-700">{t('collectionWorkflow.historyLoadError')} <button onClick={loadInteractions} className="underline">{t('common.retry', 'Retry')}</button></div> : t('customerBot.noInteractions', 'No interactions yet')}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && <div role="alert" className="text-red-700">{t('collectionWorkflow.historyLoadError')} <button onClick={loadInteractions} className="underline">{t('common.retry', 'Retry')}</button></div>}
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        {t('customerBot.interactionLog', 'Interaction History')}
      </h3>
      <div className="max-h-96 space-y-2 overflow-y-auto">
        {interactions.map((item) => (
          <div
            key={item.interaction_id}
            className="flex items-start gap-2 rounded-lg bg-gray-50 p-2 text-sm hover:bg-gray-100"
          >
            <span className="text-lg flex-shrink-0">
              {getDirectionIcon(item.direction)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-800">
                  {getTypeLabel(item.message_type)}
                </span>
                <span className="rounded-full bg-white px-1.5 py-0.5 text-xs text-gray-500">
                  {getChannelLabel(item.channel)}
                </span>
                <span className={`text-xs ${getStatusColor(item.status)}`}>
                  • {item.status === 'attempted' || item.selected_action === 'debt_report_handoff_opened' ? t('collectionWorkflow.attempted') : item.status === 'accepted' ? t('collectionWorkflow.accepted') : item.status}
                </span>
              </div>
              {item.destination && <p className="text-xs text-gray-700">{item.contact_name} {item.contact_role} <bdi>{item.destination}</bdi></p>}
              {item.message_text && (
                <p className="mt-0.5 truncate text-xs text-gray-600">
                  {item.message_text}
                </p>
              )}
              {item.related_invoice_ids && item.related_invoice_ids.length > 0 && (
                <p className="mt-0.5 text-xs text-blue-600">
                  📄 {item.related_invoice_ids.join(', ')}
                </p>
              )}
              {item.error_details && (
                <p className="mt-0.5 text-xs text-red-500">{item.error_details}</p>
              )}
            </div>
            <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
              {formatTimestamp(item.timestamp)}
            </span>
          </div>
        ))}
      </div>
      {lastKey && (
        <button
          onClick={loadMore}
          className="w-full py-2 text-center text-sm text-blue-600 hover:text-blue-800"
        >
          {t('common.loadMore', 'Load more')}
        </button>
      )}
    </div>
  );
};

export default CustomerInteractionLog;
